/*
  Pacific Formulations — scroll reveal

  Anything marked [data-pf-reveal] rises and fades in once, when it first
  enters the viewport. Stagger by setting --pf-reveal-i on the element; the
  delay is applied in CSS so nothing is timed in JS.

  Two rules this follows:

  1. Content is never hidden unless this file is running. The hidden state is
     scoped to html.pf-reveal-ready, and that class is added by an inline
     snippet in <head> — so if JS fails, is blocked, or the file 404s, every
     element simply stays visible instead of the page going blank.

  2. Reduced motion is honoured by never arming at all. The inline snippet
     checks the media query before adding the class, so a visitor with reduced
     motion gets the finished page with no transition to interrupt, rather than
     an instant jump.

  Elements are unobserved after revealing: this is an entrance, not a scroll
  effect, and re-animating on the way back up reads as a glitch.
*/
(() => {
  const targets = document.querySelectorAll('[data-pf-reveal]');
  if (!targets.length) return;

  // Belt and braces: if the head snippet did not arm (reduced motion, or an
  // older browser), there is nothing to reveal because nothing was hidden.
  if (!document.documentElement.classList.contains('pf-reveal-ready')) return;

  if (!('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  // Two modes. The default is an entrance: reveal once, stop observing —
  // re-animating on the way back up reads as a glitch on text. Opt in with
  // data-pf-reveal="toggle" for something that should undo itself as it
  // leaves (the pillar deck re-stacks): it keeps observing, and on exit swaps
  // .is-revealed for .is-leaving so the CSS can play the reverse. .is-leaving
  // is only ever added AFTER a reveal, so a first visit never plays an exit.
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const toggle = el.dataset.pfReveal === 'toggle';
        // Announce a flip BEFORE the classes change, synchronously, so a
        // section can snapshot where its animations are and continue them
        // from there rather than restarting. Listeners run to completion
        // before the class swap below.
        if (toggle) {
          const entering = entry.isIntersecting;
          const wasIn = el.classList.contains('is-revealed');
          if (entering !== wasIn) {
            el.dispatchEvent(new CustomEvent('pf:reveal-flip', { detail: { entering } }));
          }
        }
        let flipped = false;
        if (entry.isIntersecting) {
          flipped = toggle && !el.classList.contains('is-revealed');
          el.classList.remove('is-leaving');
          el.classList.add('is-revealed');
          if (!toggle) io.unobserve(el);
        } else if (toggle && el.classList.contains('is-revealed')) {
          el.classList.remove('is-revealed');
          el.classList.add('is-leaving');
          flipped = true;
        }
        // The matching AFTER-swap announcement, still synchronous: the new CSS
        // animation now exists (getAnimations() flushes style), and nothing
        // has painted yet — so a listener can seek it before the first frame.
        // A section that seeks in requestAnimationFrame instead gets one frame
        // of the animation at time zero: a visible flash.
        if (flipped) {
          el.dispatchEvent(new CustomEvent('pf:reveal-flipped', { detail: { entering: entry.isIntersecting } }));
        }
      });
    },
    // A little short of the fold, so the movement finishes as the element
    // arrives rather than starting once the reader is already looking at it.
    //
    // Note for anyone tempted to add a horizontal margin here to reach cards
    // in a scrolling row: it does not work. An ancestor with overflow clips
    // the target to zero area before the root rect is considered, so the card
    // never intersects however far the root is expanded. Reveal such rows as a
    // single element instead.
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  targets.forEach((el) => io.observe(el));
})();
